import { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Navigate, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router'

import {
  billingRoutePath,
  profileRoutePath,
  signInRoutePath,
} from 'config/routePaths'
import AppHeader from 'Layout/AppHeader'
import { usePageTitle } from 'Layout/DocumentTitle'
import useCurrentAccount from 'query/useCurrentAccount'
import useCurrentUser from 'query/useCurrentUser'
import {
  clearSentryContext,
  setSentryErrorBoundaryContext,
  syncSentryContext,
} from 'utils/sentry'
import { clearSessionData } from 'utils/session'
import { isAdmin, isPaidAccount } from 'utils/validation'

const pageLocationFromRoute = location =>
  new URL(`${location.pathname}${location.search}${location.hash}`, window.location.href).href

export const GoogleAnalytics = () => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const pageTitle = usePageTitle()
  const previousPathnameRef = useRef(location.pathname)

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) {
      return
    }

    previousPathnameRef.current = location.pathname

    const gtag = window.gtag

    if (navigationType !== 'PUSH' || typeof gtag !== 'function') {
      return
    }

    gtag('config', 'UA-847542-4', {
      page_location: pageLocationFromRoute(location),
      page_path: `${location.pathname}${location.search}`,
      page_title: pageTitle,
    })
  }, [location, navigationType, pageTitle])

  return null
}

const unpaidAdminAllowedRoutePaths = [billingRoutePath, profileRoutePath]
export const getProtectedRedirectPath = ({ currentAccount, currentUser, pathname }) => {
  if (!currentUser?.id) {
    return null
  }

  if (!isAdmin(currentUser)) {
    return pathname === profileRoutePath ? null : profileRoutePath
  }

  if (!currentAccount?.id || isPaidAccount(currentAccount)) {
    return null
  }

  return unpaidAdminAllowedRoutePaths.includes(pathname) ? null : billingRoutePath
}

export const AppMain = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const routeErrorBoundaryRef = useRef(null)
  const hasRouteErrorRef = useRef(false)
  const { data: currentUser = {}, isError: isCurrentUserError } = useCurrentUser()
  const { data: currentAccount = {}, isError: isCurrentAccountError } = useCurrentAccount({
    enabled: Boolean(currentUser?.id && isAdmin(currentUser)),
  })

  useEffect(() => {
    syncSentryContext({
      currentAccount,
      currentUser,
      pathname: location.pathname,
    })
  }, [currentAccount, currentUser, location.pathname])

  useEffect(() => () => {
    clearSentryContext()
  }, [])

  useEffect(() => {
    if (isCurrentUserError || isCurrentAccountError) {
      clearSessionData()
      navigate(signInRoutePath, { replace: true })
    }
  }, [isCurrentAccountError, isCurrentUserError, navigate])

  useEffect(() => {
    if (!hasRouteErrorRef.current) {
      return
    }

    routeErrorBoundaryRef.current?.resetErrorBoundary()
    hasRouteErrorRef.current = false
  }, [location.pathname])

  if (!currentUser?.id) {
    return null
  }

  const redirectPath = getProtectedRedirectPath({
    currentAccount,
    currentUser,
    pathname: location.pathname,
  })

  if (redirectPath) {
    return <Navigate replace to={redirectPath} />
  }

  if (isAdmin(currentUser) && !currentAccount?.id) {
    return null
  }

  return (
    <div className="tw:min-h-screen tw:bg-background">
      <a
        className="tw:fixed tw:left-4 tw:top-4 tw:z-[100] tw:-translate-y-24 tw:rounded-card tw:bg-surface-raised tw:px-4 tw:py-2 tw:font-semibold tw:text-foreground-strong tw:shadow-md tw:focus:translate-y-0"
        href="#main-content"
      >
        Skip to content
      </a>
      <AppHeader />
      <main id="main-content" tabIndex={-1}>
        <Sentry.ErrorBoundary
          beforeCapture={scope => {
            setSentryErrorBoundaryContext({
              currentAccount,
              currentUser,
              pathname: location.pathname,
              scope,
            })
          }}
          onError={() => {
            hasRouteErrorRef.current = true
          }}
          ref={routeErrorBoundaryRef}
        >
          <Outlet />
        </Sentry.ErrorBoundary>
        <GoogleAnalytics />
      </main>
    </div>
  )
}

export default AppMain
